import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchData, deleteData, postData } from '../../utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const UsersList = () => {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);

  const alertConfigurations = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
  };

  // Fetch users data
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['companyUsers', companySlug, currentPage],
    queryFn: () => fetchData(`api/companies/${companySlug}/user_invitations`, { page: currentPage }),
    enabled: !!companySlug
  });

  // Remove user mutation
  const removeUserMutation = useMutation({
    mutationFn: (userId) => deleteData(`api/companies/${companySlug}/user_invitations/${userId}`),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries(['companyUsers', companySlug]);
        toast.success(result.message, alertConfigurations);
      } else {
        toast.error(result.error || 'Failed to remove user', alertConfigurations);
      }
    },
    onError: (error) => {
      console.error('Remove user error:', error);
      toast.error(error.response?.data?.error || 'Failed to remove user', alertConfigurations);
    },
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: (userId) => postData(`api/companies/${companySlug}/user_invitations/${userId}/resend`),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message, alertConfigurations);
      } else {
        toast.error(result.error || 'Failed to resend invitation', alertConfigurations);
      }
    },
    onError: (error) => {
      console.error('Resend invitation error:', error);
      toast.error(error.response?.data?.error || 'Failed to resend invitation', alertConfigurations);
    },
  });

  const handleRemoveUser = (userId) => {
    const userToDelete = users.find(user => user.id === userId);
    
    if (userToDelete && userToDelete.status === 'Invited') {
      if (window.confirm('Are you sure you want to remove this user from the company?')) {
        removeUserMutation.mutate(userId);
      }
      return;
    }
    
    const activeUsers = users.filter(user => user.status !== 'Invited' && user.status !== 'Expired');
    if (activeUsers.length <= 1) {
      toast.error('Cannot delete user. Company must have at least one owner.', alertConfigurations);
      return;
    }
    
    if (window.confirm('Are you sure you want to remove this user from the company?')) {
      removeUserMutation.mutate(userId);
    }
  };

  const handleResendInvitation = (userId) => {
    resendInvitationMutation.mutate(userId);
  };

  const handleInviteUser = () => {
    navigate(`/companies/${companySlug}/users/invite`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (isLoading) return (
    <div className="loading-container">
      <span className="loader"></span>
    </div>
  );

  if (isError) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">
          Error loading users: {error?.response?.data?.error || error.message}
        </div>
      </div>
    );
  }

  const { users = [], company = {}, pagination = {} } = data || {};

  const canDeleteUser = (userToDelete) => {
    if (userToDelete && (userToDelete.status === 'Invited' || userToDelete.status === 'Expired')) {
      return true;
    }
    
    const activeUsers = users.filter(user => user.status !== 'Invited' && user.status !== 'Expired');
    return activeUsers.length > 1;
  };

  const renderPagination = () => (
    <div className="users-pagination">
      <div className="pagination-wrapper">
        <div className="btn-group" role="group">
          <button
            className="btn pagination-btn px-4 rounded-pill"
            onClick={() => handlePageChange((pagination.current_page || currentPage) - 1)}
            disabled={(pagination.current_page || currentPage) === 1}
            type="button"
          >
            Previous
          </button>

          <div className="dropdown mx-2">
            <button
              className="pagination-btn px-4 dropdown-toggle custom-pagination-btn rounded-pill"
              type="button"
              id="pageDropdown"
              data-bs-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              Page {pagination.current_page || currentPage}
            </button>
            <div className="dropdown-menu dropdown-menu-scrollable" aria-labelledby="pageDropdown">
              {Array.from({ length: pagination.total_pages }, (_, i) => (
                <button
                  key={i + 1}
                  className={`dropdown-item btn btn-pagination rounded-pill ${(pagination.current_page || currentPage) === i + 1 ? 'active' : ''}`}
                  type="button"
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>

          <button
            className="btn pagination-btn rounded-pill px-4"
            onClick={() => handlePageChange((pagination.current_page || currentPage) + 1)}
            disabled={(pagination.current_page || currentPage) === pagination.total_pages}
            type="button"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="users-management-container">
      <div className="users-page">
        <div className="d-flex justify-content-between align-items-center mb-3 mt-5">
          <h1>Users</h1>
          <button 
            className="btn btn-outline-primary rounded-pill px-4 py-3 border-2px font-"
            onClick={handleInviteUser}
            type="button"
          >
            Invite User
          </button>
        </div>

        {users.length === 0 ? (
          <div className="empty-state">
            <p>No users found.</p>
            <button 
              className="btn btn-outline-primary rounded-pill px-4"
              onClick={handleInviteUser}
              type="button"
            >
              Invite Your First User
            </button>
          </div>
      ) : (
        <>
        <div className=''>
          <div className="table-responsive">
            <table className="table table-borderless">
              <thead>
                <tr className="border-bottom">
                  <th className="fw-normal">Email</th>
                  <th className="fw-normal">Status</th>
                  <th className="fw-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-bottom">
                    <td className="py-3">
                      <span className="text-primary">{user.email}</span>
                      {user.is_current_user && (
                        <span className="text-primary ms-1">(Me)</span>
                      )}
                    </td>
                    <td className="py-3">
                      { (!user.is_current_user) && 
                        <span className={'text-warning'}>
                          {user.status}
                        </span>
                      }
                    </td>
                    <td className="py-3">
                      <div className="d-flex gap-2 align-items-center">
                        {!user.is_current_user && user.status === 'Invited' && (
                          <button
                            className="btn-action mr-2"
                            onClick={() => handleResendInvitation(user.id)}
                            disabled={resendInvitationMutation.isLoading}
                            type="button"
                          >
                            {resendInvitationMutation.isLoading ? 'Sending...' : 'Resend Invite'}
                          </button>
                        )}
                        {!user.is_current_user && canDeleteUser(user) && (
                          <button
                            className="btn-action"
                            onClick={() => handleRemoveUser(user.id)}
                            disabled={removeUserMutation.isLoading}
                            type="button"
                          >
                            {removeUserMutation.isLoading ? 'Removing...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>

          {/* Pagination */}
          {/* {pagination.total_pages > 1 && renderPagination()} */}
          {renderPagination()}
        </>
        )}
      </div>
    </div>
  );
};

export default UsersList;